import React, { ChangeEvent, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Alert.module.css';
import { EntityId, nanoid } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router-dom';

import Uppy from '@uppy/core';
import Tus from '@uppy/tus';

import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../../hooks/useSimpleBar';
import { useForm, FormProvider } from 'react-hook-form';

import { IModal } from '../../../types/modal';
import { IndexItem, Options, ProjectShare } from '../../../types/library';

import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { useSlice } from '../../../hooks/useSlice';
import useModal from '../../../hooks/useModal';
import { usePage } from '../../../hooks/usePage';

import {
  libApiSlice,
  useUpdateShareLinkMutation,
  useUpdateBookInfoMutation,
  useUpdateIndexMutation,
  useUpdatePublicMutation,
  useUpdateShareUserMutation,
  useUpdateCopyrightMutation,
} from '../../../services/library';
import { useAddSharedLinkMutation } from '../../../services/share';
import {
  useUpdateResourceMutation,
  useValidateDuplicationMutation,
  useUpdateUrlMutation,
  useUploadLinkMutation,
  useAddDriveSharingMutation,
  useSubmitResourceMutation,
} from '../../../services/drive';
import { store } from '../../../services/store';
import { linkApiSlice } from '../../../services/link';
import {
  studentApiSlice,
  useAddStudentMutation,
} from '../../../services/student';
import { useAddGroupMutation } from '../../../services/group';

import {
  groupKeywordChanged,
  groupListAdded,
  groupTotalCntAdded,
  studentKeywordChanged,
  studentPageChanged,
} from '../../../features/classroomSlice';

import Modal from '../Modal';
import Button from '../../Button';
import BookInfo from './BookInfo';
import BookIndex from './BookIndex/BookIndex';
import RegInfo from './RegInfo';
import FileShare from './FileShare';
import FileRelease from './FileRelease';
import History from './History';
import TipButton from '../../TipButton';
import ConditionalWrapper from '../../ConditionalWrapper';
import LinkNotification from './LinkNotification';
import AddStudent from './AddStudent';
import AddGroup from './AddGroup';

import {
  MODAL_TITLE_LIST,
  CLOSE_POPUP,
  COMPLETE,
  DELETE_HISTORY,
  CONFIRM,
  SHOW_ALL_LINK_NOTI,
  MOBILE_FILE_SHARE_GUIDE,
  EMPTY_CONTENT,
  HIDE_FOR_TODAY,
  CLOSE,
} from '../../../constants';

import { isOfType } from '../../../utils/typeGuard';
import { getAccessToken, getTokens, setToken } from '../../../utils/storage';
import { VITE_API_V3, download, isMobile } from '../../../utils/util';
import { libShareUserChanged } from '../../../features/librarySlice';

import { openSelpubCloud } from '../../../utils/selpub';
import {
  useValidateResourceLinkMutation,
  useValidateSharedLinkMutation,
} from '../../../services/guest';
import { shareLinkPageChanged } from '../../../features/settingsSlice';
import Copyright from './Copyright';
import { deleteCookie, setCookie } from '../../../utils/cookie';
import projectCopyright from './projectCopyright';
import { TooltipWrapper } from '../../Tooltip';

interface Content {
  [key: string]: (data: any) => React.ReactElement | null;
}

const ContentList: Content = {
  project: BookInfo,
  projectCopyright,
  projectIndex: BookIndex,
  projectShare: FileShare,
  projectPublic: FileRelease,
  projectHistoryList: History,
  linkRegistration: RegInfo,
  linkPassword: RegInfo,
  linkNotification: LinkNotification,
  dataRegistration: RegInfo,
  resource: RegInfo,
  url: RegInfo,
  share: FileShare,
  addStudent: AddStudent,
  addGroup: AddGroup,
  uploadCopyright: Copyright,
};

export default function Alert({
  name,
  id,
  type,
  initialButtonDisabled = false,
  data: receivedData,
  subKey,
  contentTitle,
}: IModal) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const { pageChanged, initPageList } = usePage();
  const navigate = useNavigate();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const {
    selectSingleItem,
    selectSelectedId,
    selectOptions,
    indexListChanged,
  } = useSlice();
  const { sortType, sortOrder }: Options = useAppSelector(selectOptions);
  const selectedItem = useAppSelector(selectSingleItem);
  const projectId = useAppSelector(selectSelectedId);
  const [isDisabledSubmit, setIsDisabledSubmit] = useState(
    initialButtonDisabled
  );
  const [msg, setMsg] = useState('');
  const [resData, setResData] = useState(null);
  const [isPending, setIspPending] = useState(false);

  const [simplebarRef] = useSimpleBar();

  const location = useLocation();
  const folderId = location?.state?.folderId;

  const [updateBookInfo] = useUpdateBookInfoMutation();
  const [updateCopyright] = useUpdateCopyrightMutation();
  const [updateIndex] = useUpdateIndexMutation({
    fixedCacheKey: 'updated-index',
  });
  const [updateShareLink] = useUpdateShareLinkMutation();
  const [updateShareUser] = useUpdateShareUserMutation();
  const [updatePublic] = useUpdatePublicMutation();
  const [addSharedLink] = useAddSharedLinkMutation();
  const [uploadLink] = useUploadLinkMutation();
  const [submitResource] = useSubmitResourceMutation();
  const [upodateResource] = useUpdateResourceMutation();
  const [upodateUrl] = useUpdateUrlMutation();
  const [addDriveSharing] = useAddDriveSharingMutation();
  const [validateDuplication] = useValidateDuplicationMutation();
  const [getLink] = linkApiSlice.useLazyGetLinkQuery();
  const [addStudent] = useAddStudentMutation();
  const [getStudent] = studentApiSlice.useLazyGetStudentQuery();
  const [addGroup] = useAddGroupMutation();
  const [validateLink] = useValidateSharedLinkMutation();
  const [validateResourceLink] = useValidateResourceLinkMutation();

  const { toggleModal } = useModal();
  const closeModal = (e?: React.MouseEvent, isOK?: boolean) => {
    if (id === 'linkPassword') {
      if (
        closeButtonRef.current === e?.currentTarget &&
        location.state?.userType !== 'guest'
      ) {
        if (receivedData.isResource) {
          navigate('/');
        } else {
          navigate('/home');
        }
      } else if (isOK) {
        if (receivedData.isResource) {
          download(receivedData.projectId, true);
          navigate('/');
        } else {
          openSelpubCloud(
            'viewer',
            receivedData.projectId,
            !location.pathname.includes('home'),
            undefined,
            true
          );
        }
      }
    }
    toggleModal({ id, type });
  };

  const methods = useForm();
  const {
    formState: { isValid },
    handleSubmit,
  } = methods;

  const Content = ContentList[id];
  const hasTitle = ![
    'projectIndex',
    'projectHistoryList',
    'linkRegistration',
    'linkNotification',
    'dataRegistration',
    'resource',
    'url',
    'addStudent',
    'addGroup',
  ].includes(id);
  const title =
    contentTitle ||
    (isOfType(selectedItem, 'project')
      ? selectedItem.project.title
      : selectedItem?.resource.orgFileName);

  const isHistoryList = id === 'projectHistoryList';
  const isLinkNoti = id === 'linkNotification';

  // TODO: Set data type
  const onSubmit = async (data: any) => {
    console.log('sumbmit data : ', data);
    if (isPending) return;

    if (
      (id === 'projectShare' && !data?.shareLink && !data?.shareUser) ||
      (id === 'share' && !data.hasOwnProperty('link')) ||
      ['projectHistoryList', 'linkNotification', 'uploadCopyright'].includes(id)
    ) {
      closeModal();
      return;
    }

    try {
      if (onSubmitHandlers[id]) {
        if (id === 'project') {
          if (data.title.trim() === '') {
            data.title = EMPTY_CONTENT;
          }
          if (!isMobile && !+data.orgCoverYn && data.updateCover) {
            const responseMap = new Map();
            const uppy = new Uppy({
              autoProceed: true,
            })
              .use(Tus, {
                removeFingerprintOnSuccess: true,
                async onBeforeRequest(req) {
                  req.setHeader('Authorization', `Bearer ${getAccessToken()}`);
                },
                onShouldRetry(err, retryAttempt, options, next) {
                  if (
                    isOfType(err, 'originalResponse') &&
                    err.originalResponse.getStatus() === 401
                  ) {
                    return true;
                  }
                  return next(err);
                },
                async onAfterResponse(req, res) {
                  if (res.getStatus() === 200) {
                    if (res.getBody() !== '') {
                      responseMap.set(req.getURL(), JSON.parse(res.getBody()));
                    }
                  }
                },
                endpoint: `${VITE_API_V3}/common/resources/upload/tus`,
              })
              .on('file-added', (file) => {
                console.log('file-added', file);
                uppy.setMeta({ orgCoverYn: 0 });
              })
              .on('complete', (result) => {
                console.log('complete : ', result);
                result.successful.forEach(async (success) => {
                  const { resourceId } = responseMap.get(
                    success.uploadURL
                  ).data;
                  try {
                    await store.dispatch(
                      libApiSlice.endpoints.bindNewCover.initiate({
                        projectId,
                        resourceId,
                      })
                    );
                  } catch (error) {
                    console.error('Failed to upload new cover', error);
                  }
                });
                uppy.close();
              })
              .on('upload-error', (file, err, res) => {
                console.log('upload-error : ', file, err, res);
                if (err.message.includes('401_2')) {
                  setToken(getTokens());
                  uppy.retryAll();
                }
              });
            uppy.addFile(data.updateCover[0]);

            delete data.updateCover;
          } else if (isMobile) {
            data.orgCoverYn = 1;
          }
        } else if (id === 'projectIndex') {
          const filteredList = data.indexList.filter(
            ({ content, page }: IndexItem) => content !== ''
          );
          data.indexList = filteredList.map(
            ({ indentYn, content, page }: IndexItem) => {
              if (page === '') {
                page = 1;
              }
              return { indentYn, content, page };
            }
          );
        } else if (id === 'dataRegistration') {
          const isExist = await getIsExist(data.name);
          if (isExist) {
            toggleModal({
              id: 'confirmFileReplace',
              type: 'confirm',
              data: { type: 'web', name: data.name, url: data.url },
            });
            return;
          }
        } else if (id === 'linkRegistration') {
          const url = new URL(data.link);
          const searchParams = new URLSearchParams(url.search);

          data.link = searchParams.get('link');

          if (!data.pwd) {
            try {
              const linkRes = await getLink(data.link);
              if (linkRes.data?.pwdYn) {
                setResData(linkRes.data.pwdYn);
                setIsDisabledSubmit(!data.pwd);
                setMsg('');
                return;
              }
            } catch (error) {
              console.error('Failed to get link', error);
            }
          }
        } else if (id === 'linkPassword') {
          const { link, isResource, projectId, resourceId } = receivedData;
          data = {
            ...data,
            link: link,
            ...(isResource ? { resourceId } : { projectId }),
          };
        } else if (id === 'projectShare') {
          if (data.shareLink) {
            !data.shareLink.expDate && (data.shareLink.expDateYn = 0);
            !data.shareLink.pwd && (data.shareLink.pwdYn = 0);
          }

          if (data.shareUser && !data.shareUser.expDate) {
            data.shareUser.expDateYn = 0;
            dispatch(libShareUserChanged(data.shareUser));
          }
          updateFileShare({ projectId: projectId as EntityId, data });
          return;
        } else if (id === 'share') {
          !data.expDate && (data.expDateYn = 0);
          data.pwd || (data.pwdYn = 0);
        }

        setIspPending(true);
        const res = await onSubmitHandlers[id]({
          projectId,
          data,
        }).unwrap();
        setIspPending(false);

        if (id === 'dataRegistration') {
          const { resourceId } = res.data;
          await submitResource({
            folderId,
            overwriteYn: 1,
            resourceIdList: [resourceId],
          }).unwrap();
        }
        console.log('res.message : ', res.message);
        setMsg(res.message);
        res.message === 'OK' && closeModal(undefined, true);

        if (pageChanged) {
          setTimeout(
            () => {
              dispatch(pageChanged(0));
              dispatch(
                initPageList({
                  folderId,
                  page: 0,
                  sortBy: sortType,
                  sortDir: sortOrder,
                  projectId: nanoid(),
                })
              );
            },
            id === 'project' ? 800 : 0
          );
        }

        if (id === 'projectIndex') {
          dispatch(indexListChanged(res.data.indexList));
        } else if (id === 'addStudent') {
          try {
            await getStudent({}).unwrap();
            dispatch(studentPageChanged(0));
            dispatch(studentKeywordChanged(''));
          } catch (error) {
            console.error('Failed to get student', error);
          }
        } else if (id === 'addGroup') {
          try {
            dispatch(groupListAdded(res.data));
            dispatch(groupKeywordChanged(''));
            dispatch(groupTotalCntAdded());
          } catch (error) {
            console.error('Failed to get student', error);
          }
        }
      }
    } catch (error) {
      console.error('Failed to submit', error);
    }
  };

  const getIsExist = async (name: string) => {
    try {
      const res = await validateDuplication({
        folderId,
        resourceList: [{ orgFileName: name }],
      }).unwrap();
      return res.data.resourceList[0].existYn;
    } catch (error) {
      console.error('Failed to validate : ', error);
      return 0;
    }
  };

  const updateFileShare = async ({
    projectId,
    data,
  }: {
    projectId: EntityId;
    data: ProjectShare;
  }) => {
    try {
      data.shareLink &&
        (await updateShareLink({ projectId, data: data.shareLink }));
      await updateShareUser({ projectId, data: data.shareUser });
    } catch (error) {
      console.error('Failed to update file share', error);
    }
    closeModal();
  };

  const onSubmitHandlers: { [key: string]: any } = {
    project: updateBookInfo,
    projectCopyright: updateCopyright,
    projectIndex: updateIndex,
    projectShare: updateFileShare,
    projectPublic: updatePublic,
    linkRegistration: addSharedLink,
    linkPassword: receivedData?.isResource
      ? validateResourceLink
      : validateLink,
    dataRegistration: uploadLink,
    resource: upodateResource,
    url: upodateUrl,
    share: addDriveSharing,
    addStudent,
    addGroup,
  };

  const openDelHistoryModal = () => {
    toggleModal({
      id: 'projectHistoryList',
      type: 'alert',
    });
    toggleModal({ id: 'confirmDeleteHistory', type: 'confirm' });
  };

  const moveToSharedLink = () => {
    dispatch(shareLinkPageChanged(0));
    navigate('/home/settings/sharedLink');
    toggleModal({
      name: 'link-notification',
      id: 'linkNotification',
      type: 'alert',
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isFilled: boolean
  ) => {
    setIsDisabledSubmit(!isFilled);
  };

  const checkHideToday = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCookie('selpubAlertCopyrightPopup', 'hideToday', 1);
    } else {
      deleteCookie('selpubAlertCopyrightPopup');
    }
  };

  return (
    <Modal id={id} type={type}>
      <FormProvider {...methods}>
        <div className={cx('top')}>
          <h2 className={cx('tit')}>
            {MODAL_TITLE_LIST[id]}
            {!isMobile && (id === 'projectShare' || id === 'share') && (
              <TipButton name="fileShare" />
            )}
          </h2>
          <div className={cx('btn_wrap')}>
            <TooltipWrapper content={CLOSE} onClick={closeModal}>
              {(tooltipProps) => (
                <Button
                  name={CLOSE_POPUP}
                  classnames={['pop_close', 'modal']}
                  isBlindName={true}
                  ref={closeButtonRef}
                  tooltipProps={tooltipProps}
                >
                  <i className={cx('svg_i')}></i>
                </Button>
              )}
            </TooltipWrapper>
          </div>
        </div>

        <div className={cx('content', id)}>
          {isMobile && id === 'projectShare' && (
            <p className={cx('mobile_guide_txt')}>{MOBILE_FILE_SHARE_GUIDE}</p>
          )}
          {hasTitle && <p className={cx('book_title')}>{title}</p>}
          <ConditionalWrapper
            condition={id !== 'projectShare' && id !== 'project'}
            wrapper={(children) => (
              <SimpleBar forceVisible={true} ref={simplebarRef}>
                {children}
              </SimpleBar>
            )}
          >
            <Content
              name={name}
              id={id}
              type={type}
              subKey={subKey}
              onChange={handleInputChange}
              message={msg}
              data={resData}
            />
          </ConditionalWrapper>

          <div
            className={cx('btn_wrap', {
              mt6: id === 'linkRegistration' && msg !== '',
            })}
          >
            <Button
              type="submit"
              name={
                ['linkNotification', 'uploadCopyright'].includes(id)
                  ? CONFIRM
                  : COMPLETE
              }
              classnames={[
                'b_t',
                'edit_complete',
                { 'is--disabled': isDisabledSubmit },
              ]}
              handleClick={handleSubmit(onSubmit)}
              disabled={!isValid}
            ></Button>
            {(isHistoryList || isLinkNoti) && (
              <Button
                name={isHistoryList ? DELETE_HISTORY : SHOW_ALL_LINK_NOTI}
                classnames={[
                  'skyt',
                  'alert_sub',
                  { del_activity: isHistoryList },
                  { view_notice: isLinkNoti },
                  {
                    'btn--disabled':
                      isHistoryList &&
                      isOfType(selectedItem, 'projectHistoryList') &&
                      !selectedItem.projectHistoryList.length,
                  },
                ]}
                handleClick={
                  isHistoryList ? openDelHistoryModal : moveToSharedLink
                }
              />
            )}
          </div>
        </div>
        {id === 'uploadCopyright' && (
          <div className={cx('hide_today')}>
            <label>
              <input
                type="checkbox"
                className={cx('blind')}
                onChange={checkHideToday}
              />
              <span className={cx('clabel')}>{HIDE_FOR_TODAY}</span>
            </label>
          </div>
        )}
      </FormProvider>
    </Modal>
  );
}
